import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import PlayCircleRoundedIcon from '@mui/icons-material/PlayCircleRounded';
import { Box, Button, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import { courseIcons } from '../components/courseIcons';
import ProgressRing from '../../student/components/ProgressRing';
import { completedLessonCount, coursePercent, mockCourses, StudentCourse } from '../../student/mockData';
import { BLUE, cardSx, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SUCCESS_TEXT, SURFACE_SOFT } from '../../student/studentStyles';

function CourseCard({ course, onOpen }: { course: StudentCourse; onOpen: () => void }) {
    const CourseIcon = courseIcons[course.icon];
    const percent = coursePercent(course);
    const complete = completedLessonCount(course);
    const allDone = complete === course.lessons.length;
    const nextLesson = course.lessons.find(lesson => lesson.completedPrompts < lesson.totalPrompts);

    return (
        <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', minHeight: 310 }}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Box sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: '16px', display: 'grid', placeItems: 'center', bgcolor: `${course.accentColor}1A` }}>
                    <CourseIcon sx={{ color: course.accentColor, fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, lineHeight: 1.3, color: NAVY }}>{course.name}</Typography>
                    <Typography sx={{ mt: 0.35, fontFamily: INTER, fontSize: 12.5, color: SLATE }}>{course.subtitle}</Typography>
                </Box>
                <ProgressRing percent={percent} size={54} thickness={5} />
            </Stack>

            <Box sx={{ mt: 2.5 }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 0.75 }}>
                <Typography sx={{ flex: 1, fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: allDone ? SUCCESS_TEXT : SLATE }}>{allDone ? 'COURSE COMPLETE' : 'EXERCISE PROGRESS'}</Typography>
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: SLATE }}>{percent}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={percent} sx={{ height: 7, borderRadius: 8, bgcolor: RING_TRACK, '& .MuiLinearProgress-bar': { borderRadius: 8, bgcolor: allDone ? GREEN : course.accentColor } }} />
            </Box>

            <Box sx={{ mt: 2.5, p: 1.75, borderRadius: '14px', bgcolor: SURFACE_SOFT }}>
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: SLATE }}>{allDone ? 'LAST LESSON' : 'UP NEXT'}</Typography>
                <Typography sx={{ mt: 0.45, fontFamily: INTER, fontWeight: 700, fontSize: 13, color: NAVY }}>{nextLesson?.name ?? course.lessons[course.lessons.length - 1].name}</Typography>
            </Box>

            <Button onClick={onOpen} endIcon={<ArrowForwardRoundedIcon />} sx={{ mt: 'auto', pt: 2.5, alignSelf: 'flex-start', px: 0, fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE, textTransform: 'none' }}>View lessons</Button>
        </Box>
    );
}

export default function StudentDesktopCoursesPage({ onOpenCourse }: { onOpenCourse: (courseId: number) => void }) {
    const totalLessons = mockCourses.reduce((sum, course) => sum + course.lessons.length, 0);
    const completeLessons = mockCourses.reduce((sum, course) => sum + completedLessonCount(course), 0);
    const inProgressLessons = mockCourses.reduce((sum, course) => sum + course.lessons.filter(lesson => lesson.completedPrompts > 0 && lesson.completedPrompts < lesson.totalPrompts).length, 0);

    return (
        <Box sx={{ maxWidth: 1240, mx: 'auto', p: { xs: 2, sm: 3, lg: 4 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-end' }} spacing={2} sx={{ mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 25, md: 30 }, color: NAVY }}>My courses</Typography>
                    <Typography sx={{ mt: 0.4, fontFamily: INTER, fontSize: 14, color: SLATE }}>Keep learning where you left off, or review a completed lesson.</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Chip icon={<CheckCircleRoundedIcon />} label={`${completeLessons} complete`} sx={{ bgcolor: `${GREEN}12`, color: SUCCESS_TEXT, fontFamily: INTER, fontWeight: 700, '& .MuiChip-icon': { color: GREEN } }} />
                    <Chip icon={<PlayCircleRoundedIcon />} label={`${inProgressLessons} in progress`} sx={{ bgcolor: `${BLUE}0D`, color: BLUE, fontFamily: INTER, fontWeight: 700, '& .MuiChip-icon': { color: BLUE } }} />
                </Stack>
            </Stack>

            <Box sx={{ ...cardSx, mb: 2.5, p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '14px', display: 'grid', placeItems: 'center', bgcolor: `${BLUE}0D` }}><MenuBookRoundedIcon sx={{ color: BLUE }} /></Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY }}>{mockCourses.length} active courses</Typography>
                    <Typography sx={{ mt: 0.2, fontFamily: INTER, fontSize: 12.5, color: SLATE }}>{completeLessons} of {totalLessons} assigned lessons completed</Typography>
                </Box>
                <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontFamily: INTER, fontWeight: 700, fontSize: 12, color: SLATE }}>CURRENT TERM</Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 2.5 }}>
                {mockCourses.map(course => <CourseCard key={course.id} course={course} onOpen={() => onOpenCourse(course.id)} />)}
            </Box>
        </Box>
    );
}
